import { observable, makeObservable, action, runInAction } from 'mobx'
import { Auth } from 'aws-amplify'
import { CognitoUserPool, CognitoUser, CognitoRefreshToken } from 'amazon-cognito-identity-js'
import dayjs from 'dayjs'
import { apiConf, UserApi, ConfigApi } from '../api/index'
import Env from '../configs/env'
import Consts from '../configs/consts'
import { updateMailAddressEmail } from '../configs/email'
import { defaultFetch } from '../libs/fetch'

let store = null

export class AuthStore {
  expired = null
  user = null
  plan = null

  constructor(context) {
    // Amplify Auth の設定はブラウザ環境でのみ実行する（SSRでwindow未定義となるため）
    if (typeof window !== 'undefined') {
      Auth.configure({
        region: Env.cognito.region,
        userPoolId: Env.cognito.userPoolId,
        userPoolWebClientId: Env.cognito.clientId,
      })
    }
    makeObservable(this, {
      expired: observable,
      user: observable,
      login: action,
      logout: action,
      checkUser: action,
      updateEmailSubmit: action,
      confirmLogin: action,
      restoreCognitoUser: action,
    })
  }

  // ログイン
  login = async (id, password, skipApiLogin = false) => {
    try {
      await this.logout()
      this.cognitoUser = await Auth.signIn(id, password, {
        userAgent: navigator.userAgent,
      })

      // パスワードの更新または2段階認証が必要な場合
      if (this.cognitoUser.challengeName) {
        return this.cognitoUser
      }

      if (!skipApiLogin) {
        await this.apiLogin(this.cognitoUser)
      }

      this.expired = false
    } catch (err) {
      let errorMessage = 'ログインに失敗しました'
      if (err instanceof Response) {
        const errorJson = await err.json()
        if (errorJson.message === 'UserNotFoundError') {
          throw new Error('ユーザーが登録されていません')
        } else if (err.status === 401 && errorJson.message === 'User is not enabled') {
          throw new Error('このアカウントは無効になっています')
        } else if (err.status === 401) {
          if (errorJson.message === 'RegistrationUncompleteError') {
            throw new Error('RegistrationUncompleteError')
          }
          throw new Error('メールアドレスまたはパスワードが正しくありません')
        } else if (err.status === 403) {
          throw new Error('アクセスが拒否されました。ネットワーク環境をご確認ください')
        }
      } else {
        switch (err.code) {
          case 'NotAuthorizedException':
            if (err.message.indexOf('User is disabled') >= 0) {
              throw new Error('このアカウントは無効になっています')
            } else if (err.message.indexOf('Password attempts exceeded') >= 0) {
              throw new Error('一時的にログインが制限されています。しばらく時間を置いて再度お試しください')
            } else {
              throw new Error('メールアドレスまたはパスワードが正しくありません')
            }
          case 'UserNotFoundException':
            throw new Error('メールアドレスまたはパスワードが正しくありません')
          case 'UserNotConfirmedException':
            throw new Error('アカウント登録が完了していません')
          default:
            console.log(err)
            throw new Error('予期せぬエラーが発生しました。時間を置いて再度お試しください。')
        }
      }
      throw new Error(errorMessage)
    }
    return this.user
  }

  // ログアウト
  logout = async (expired = false) => {
    this.user = null
    this.plan = null
    this.expired = expired
    await Auth.signOut()
  }

  // サービスのAPIでログインを実行
  apiLogin = async (cognitoUser) => {
    const accessToken = cognitoUser.getSignInUserSession().getAccessToken().getJwtToken()
    const result = await UserApi(await apiConf(accessToken)).userCurrentGet()
    this.user = result
    // const plan = await ConfigApi(await apiConf(accessToken)).configKeyGet('plan')
    // this.plan = plan
    return this.user
  }

  getTotpSecret = async () => {
    if (!this.cognitoUser) {
      this.cognitoUser = await Auth.currentAuthenticatedUser()
    }
    const totpSecret = await Auth.setupTOTP(this.cognitoUser)
    return totpSecret
  }

  verifyTotpToken = async (code) => {
    try {
      await Auth.verifyTotpToken(this.cognitoUser, code)
    } catch (error) {
      console.warn(error)
      switch (error.code) {
        case 'CodeMismatchException':
        case 'InvalidParameterException':
        case 'EnableSoftwareTokenMFAException':
          throw new Error('認証コードが正しくありません')
      }
      throw error
    }
  }

  setupTotp = async () => {
    await Auth.setPreferredMFA(this.cognitoUser, 'TOTP')
  }

  confirmLogin = async (code) => {
    try {
      this.cognitoUser = await Auth.confirmSignIn(this.cognitoUser, code, 'SOFTWARE_TOKEN_MFA', {
        userAgent: navigator.userAgent,
      })
    } catch (error) {
      switch (error.code) {
        case 'CodeMismatchException':
          throw new Error('認証コードが正しくありません')
        case 'NotAuthorizedException':
          throw new Error('ログイン画面から再度ログインしてください')
      }
      throw error
    }
  }

  // ユーザー情報を取得
  checkUser = async () => {
    let user = null
    let plan = null
    try {
      user = await UserApi(await apiConf(this), defaultFetch).userCurrentGet()
      // plan = await ConfigApi(await apiConf(this), defaultFetch).configKeyGet('plan')
    } catch (err) {
      return { success: false, error: err }
    }
    runInAction(() => {
      this.user = user
      this.plan = plan
    })
    return { success: true, error: null }
  }

  // 認証コードを送信する
  sendVerificationEmail = async (email, password) => {
    try {
      await this.logout()
      await Auth.signIn(email, password)
    } catch {
      try {
        await Auth.signUp({
          name: email,
          username: email,
          password: password,
          attributes: {
            email,
          },
          clientMetadata: {
            subject: '【みんなの契約書】認証コードのお知らせ',
          },
        })
      } catch (err) {
        switch (err.code) {
          case 'UserNotConfirmedException':
          case 'UsernameExistsException':
            await Auth.resendSignUp(email, {
              subject: '【みんなの契約書】認証コードのお知らせ',
            })
            return
          default:
            throw err
        }
      }
    }
    throw new Error('登録済みのアカウントです。ログインしてください。')
  }

  // 検証コードでメールアドレスの検証を行う
  verifyEmail = async (email, password, code) => {
    try {
      await Auth.confirmSignUp(email, code)
      this.cognitoUser = await Auth.signIn(email, password)
    } catch (err) {
      switch (err.code) {
        case 'CodeMismatchException':
          throw new Error('認証コードが正しくありません')
        case 'NotAuthorizedException':
          await this.restoreCognitoUser()
          if (!this.cognitoUser) {
            throw new Error('登録済みのメールアドレスです。')
          }
          return
      }
      throw new Error('会員登録に失敗しました')
    }
  }

  resendVerificationEmail = async (email) => {
    try {
      await Auth.resendSignUp(email, {
        subject: '【みんなの契約書】認証コードのお知らせ',
      })
    } catch (err) {
      console.warn(err)
    }
  }

  // メールアドレスの変更リクエスト
  updateEmailRequest = async (email) => {
    try {
      const user = await Auth.currentAuthenticatedUser()
      await Auth.updateUserAttributes(user, { email }, updateMailAddressEmail)
    } catch (err) {
      switch (err.code) {
        case 'AliasExistsException':
          await UserApi(await apiConf(this)).userNotifyExistsPost({ email, type: 'update' })
          return
      }
      throw new Error('失敗しました')
    }
  }

  // 検証コードでメールアドレスの変更を確定する
  updateEmailSubmit = async (code) => {
    try {
      await Auth.verifyCurrentUserAttributeSubmit('email', code)
      this.user = await UserApi(await apiConf(this)).userCurrentEmailPost()
    } catch (err) {
      switch (err.code) {
        case 'CodeMismatchException':
          throw new Error('認証コードが正しくありません')
      }
      throw new Error('失敗しました')
    }
  }

  // パスワードを更新
  updatePassword = async (oldPassword, newPassword) => {
    try {
      const user = await Auth.currentAuthenticatedUser()
      await Auth.changePassword(user, oldPassword, newPassword)
    } catch (err) {
      switch (err.code) {
        case 'NotAuthorizedException':
          throw new Error('現在のパスワードが正しくありません')
        case 'LimitExceededException':
          throw new Error('現在のパスワードが正しくありません。しばらく時間を置いて再度お試しください')
        case 'InvalidPasswordException':
        case 'InvalidParameterException':
          throw new Error('使用できないパスワードです。')
      }
      throw new Error('失敗しました')
    }
  }

  requestResetPassword = async (userId) => {
    try {
      const data = await UserApi.forgotPassword(userId)
    } catch (err) {
      switch (err.code) {
        case 'UserNotFoundException':
          console.log(err)
          break // エラー表示は行わない
        case 'LimitExceededException':
          throw new Error('試行回数の上限に達しました。しばらくしてからもう一度お試しください。')
        case 'NotAuthorizedException':
          throw new Error('アカウントの登録が完了していません。発行した仮パスワードでログインを行ってください。')
        default:
          console.log(err)
      }
      switch (err.json.code) {
      }
    }
  }

  submitResetPassword = async (userId, verifyCode, newPassword) => {
    try {
      await Auth.forgotPasswordSubmit(userId, verifyCode, newPassword)
    } catch (error) {
      switch (error.code) {
        case 'CodeMismatchException':
          throw new Error('正しい認証コードを入力してください。')
        case 'ExpiredCodeException':
          throw new Error('認証コードが期限切れです。')
        default:
          throw error
      }
    }
  }

  // 初期パスワードを上書きする
  completeNewPassword = async (password, skipLogin = false) => {
    const cognitoUser = await Auth.completeNewPassword(this.cognitoUser, password, null, {
      userAgent: navigator.userAgent,
    })
    if (!skipLogin) {
      try {
        await this.apiLogin(cognitoUser)
      } catch (err) {
        if (err instanceof Response) {
          const errorJson = await err.json()
          if (err.status === 401) {
            if (errorJson.message === 'RegistrationUncompleteError') {
              throw new Error('RegistrationUncompleteError')
            }
          }
        }
        throw err
      }
    }
    this.cognitoUser = cognitoUser
    return cognitoUser
  }

  // 初期パスワードを更新する必要があるかどうか
  needsToCompleteNewPassword = () => {
    return this.cognitoUser && this.cognitoUser.challengeName === 'NEW_PASSWORD_REQUIRED'
  }

  // 2段階認証の設定が必要かどうか
  needsToSetupMfa = async () => {
    if (this.cognitoUser && this.cognitoUser.challengeName === 'MFA_SETUP') {
      return true
    }

    const accessToken = this.cognitoUser.getSignInUserSession().getAccessToken().getJwtToken()
    const { setupRequired, mfa } = await UserApi(await apiConf(accessToken)).userCurrentMfaGet()
    return setupRequired
  }

  // 認証アプリによるコードの入力が必要かどうか
  needsToVerifyTotp = () => {
    return this.cognitoUser && this.cognitoUser.challengeName === 'SOFTWARE_TOKEN_MFA'
  }

  // アクセストークンを取得
  getAccessToken = async () => {
    try {
      const session = await Auth.currentSession()
      return session.getAccessToken().getJwtToken()
    } catch {
      return null
    }
  }

  // IDトークンを取得
  getIdToken = async () => {
    try {
      const session = await Auth.currentSession()
      return session.getIdToken().getJwtToken()
    } catch {
      return null
    }
  }

  // アクセストークンの有効期限が切れている場合に更新を行う
  refreshTokenIfNeeded = async () => {
    try {
      const currentSession = await Auth.currentSession()
      const expiredAt = dayjs.unix(currentSession.getAccessToken().getExpiration())
      if (dayjs().isAfter(expiredAt)) {
        const refreshToken = currentSession.getRefreshToken()
        const user = await Auth.currentAuthenticatedUser()
        user.refreshSession(refreshToken, (err) => {
          if (err) throw err
        })
      }
    } catch (err) {
      console.log(err)
      return false
    }
    return true
  }

  restoreCognitoUser = async () => {
    try {
      this.cognitoUser = await Auth.currentAuthenticatedUser()
    } catch {}
  }

  cancelRegistration = async () => {
    try {
      // cognitoユーザーのカスタム属性 customerCode が登録されている場合は削除不可にする
      const userAttributes = await Auth.userAttributes(this.cognitoUser)
      const customerCode = userAttributes.find((attribute) => attribute.Name === 'custom:customerCode')
      if (customerCode) {
        throw new Error('登録が完了しているためキャンセルできません。ログイン後に退会手続きを行なってください。')
      }
      await Auth.deleteUser()
    } catch (err) {
      console.log(err)
      throw new Error('登録をキャンセルできませんでした')
    }
  }
}

export default function initStore(context) {
  if (!process.browser) {
    return new AuthStore(context)
  }
  if (!store) {
    store = new AuthStore(null)
  }
  return store
}
